import { supabase } from '../../shared/services/supabase';
import { CustomerIndividualDeadline, Customer, CustomerMonthlyStatus } from '../../shared/types';
import { calculateLohnlaufDate, calculateEffectiveLohnlaufDate, addWorkdays } from '../../shared/utils/dateUtils';

export class CustomerIndividualDeadlineService {
  /**
   * Lädt alle aktiven individuellen Deadlines für einen Kunden (wiederkehrend und einmalig)
   */
  async getCustomerIndividualDeadlines(customerId: string): Promise<CustomerIndividualDeadline[]> {
    try {
      const { data, error } = await supabase
        .from('customer_individual_deadlines')
        .select('*')
        .eq('customer_id', customerId)
        .eq('is_active', true)
        .order('is_recurring', { ascending: false }) // Wiederkehrende zuerst
        .order('day_of_month', { ascending: true })
        .order('specific_date', { ascending: true });

      if (error) {
        console.error('Error loading customer individual deadlines:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getCustomerIndividualDeadlines:', error);
      return [];
    }
  }

  /**
   * Lädt individuelle Deadlines für mehrere Kunden
   */
  async getIndividualDeadlinesForCustomers(customerIds: string[]): Promise<{ [customerId: string]: CustomerIndividualDeadline[] }> {
    if (customerIds.length === 0) return {};

    try {
      const { data, error } = await supabase
        .from('customer_individual_deadlines')
        .select('*')
        .in('customer_id', customerIds)
        .eq('is_active', true)
        .order('customer_id', { ascending: true })
        .order('is_recurring', { ascending: false })
        .order('day_of_month', { ascending: true })
        .order('specific_date', { ascending: true });

      if (error) {
        console.error('Error loading individual deadlines for customers:', error);
        return {};
      }

      // Gruppiere nach customer_id
      const groupedDeadlines: { [customerId: string]: CustomerIndividualDeadline[] } = {};
      
      (data || []).forEach(deadline => {
        if (!groupedDeadlines[deadline.customer_id]) {
          groupedDeadlines[deadline.customer_id] = [];
        }
        groupedDeadlines[deadline.customer_id].push(deadline);
      });

      return groupedDeadlines;
    } catch (error) {
      console.error('Error in getIndividualDeadlinesForCustomers:', error);
      return {};
    }
  }

  /**
   * Erstellt eine neue wiederkehrende individuelle Deadline
   */
  async createRecurringDeadline(
    customerId: string,
    title: string,
    description: string | null,
    dayOfMonth: number,
    monatOffset: number = 0,
    verschiebung: 'frueher' | 'spaeter' | null = null
  ): Promise<CustomerIndividualDeadline | null> {
    try {
      if (dayOfMonth < 1 || dayOfMonth > 31) {
        throw new Error('Tag des Monats muss zwischen 1 und 31 liegen');
      }

      const { data, error } = await supabase
        .from('customer_individual_deadlines')
        .insert([{
          customer_id: customerId,
          title: title.trim(),
          description: description?.trim() || null,
          day_of_month: dayOfMonth,
          monat_offset: monatOffset,
          verschiebung: verschiebung,
          is_recurring: true,
          is_active: true
        }])
        .select()
        .single();

      if (error) {
        console.error('Error creating recurring deadline:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in createRecurringDeadline:', error);
      return null;
    }
  }

  /**
   * Erstellt eine neue relative wiederkehrende individuelle Deadline
   * (Bezug auf einen Lohnlauf statt festem Tag). day_of_month bleibt NULL,
   * monat_offset auf DB-Default (0). bezugWerktageOffset zählt Werktage vor
   * (negativ) oder nach (positiv) dem Anker.
   */
  async createRelativeRecurringDeadline(
    customerId: string,
    title: string,
    description: string | null,
    bezugLohnlauf: 1 | 2,
    bezugWerktageOffset: number
  ): Promise<CustomerIndividualDeadline | null> {
    try {
      const { data, error } = await supabase
        .from('customer_individual_deadlines')
        .insert([{
          customer_id: customerId,
          title: title.trim(),
          description: description?.trim() || null,
          bezug_lohnlauf: bezugLohnlauf,
          bezug_werktage_offset: bezugWerktageOffset,
          is_recurring: true,
          is_active: true
        }])
        .select()
        .single();

      if (error) {
        console.error('Error creating relative recurring deadline:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in createRelativeRecurringDeadline:', error);
      return null;
    }
  }

  /**
   * NEUE METHODE: Erstellt eine neue einmalige Deadline
   */
  async createOneTimeDeadline(
    customerId: string,
    title: string,
    description: string | null,
    specificDate: Date | string
  ): Promise<CustomerIndividualDeadline | null> {
    try {
      const { data, error } = await supabase
        .from('customer_individual_deadlines')
        .insert([{
          customer_id: customerId,
          title: title.trim(),
          description: description?.trim() || null,
          specific_date: specificDate,
          is_recurring: false,
          completed: false,
          is_active: true
        }])
        .select()
        .single();

      if (error) {
        console.error('Error creating one-time deadline:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in createOneTimeDeadline:', error);
      return null;
    }
  }

  /**
   * NEUE METHODE: Aktualisiert eine individuelle Deadline
   */
  async updateDeadline(
    deadlineId: string,
    updates: Partial<CustomerIndividualDeadline>
  ): Promise<CustomerIndividualDeadline | null> {
    try {
      const { data, error } = await supabase
        .from('customer_individual_deadlines')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', deadlineId)
        .select()
        .single();

      if (error) {
        console.error('Error updating deadline:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in updateDeadline:', error);
      return null;
    }
  }

  /**
   * NEUE METHODE: Markiert eine einmalige Deadline als erledigt
   */
  async completeOneTimeDeadline(deadlineId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('customer_individual_deadlines')
        .update({
          completed: true,
          completed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', deadlineId)
        .eq('is_recurring', false);

      if (error) {
        console.error('Error completing one-time deadline:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in completeOneTimeDeadline:', error);
      return false;
    }
  }

  /**
   * NEUE METHODE: Reaktiviert eine erledigte einmalige Deadline
   */
  async uncompleteOneTimeDeadline(deadlineId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('customer_individual_deadlines')
        .update({
          completed: false,
          completed_at: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', deadlineId)
        .eq('is_recurring', false);

      if (error) {
        console.error('Error uncompleting one-time deadline:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in uncompleteOneTimeDeadline:', error);
      return false;
    }
  }

  /**
   * NEUE METHODE: Löscht (deaktiviert) eine individuelle Deadline
   */
  async deleteDeadline(deadlineId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('customer_individual_deadlines')
        .update({
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', deadlineId);

      if (error) {
        console.error('Error deleting deadline:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in deleteDeadline:', error);
      return false;
    }
  }

  /**
   * NEUE METHODE: Lädt nur einmalige Deadlines für einen Zeitraum
   */
  async getOneTimeDeadlinesForPeriod(
    customerId: string,
    startDate: Date,
    endDate: Date
  ): Promise<CustomerIndividualDeadline[]> {
    try {
      const { data, error } = await supabase
        .from('customer_individual_deadlines')
        .select('*')
        .eq('customer_id', customerId)
        .eq('is_recurring', false)
        .eq('is_active', true)
        .gte('specific_date', startDate.toISOString())
        .lte('specific_date', endDate.toISOString())
        .order('specific_date', { ascending: true });

      if (error) {
        console.error('Error loading one-time deadlines for period:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getOneTimeDeadlinesForPeriod:', error);
      return [];
    }
  }

  /**
   * Berechnet das Datum für eine individuelle Deadline
   * KORRIGIERT: Einmalige Deadlines werden immer angezeigt, wenn sie nicht erledigt sind
   *
   * Relative wiederkehrende Frist (bezug_lohnlauf gesetzt): Anker ist der
   * effektive Lohnlauf-Termin DESSELBEN Monats (kein Vormonat-Modell), plus
   * Werktage-Offset. Der Anker folgt einer ggf. markierten Datenanlieferung
   * (bezugStatus). Fehlt der referenzierte Lohnlauf beim Kunden oder ist der
   * Kunde nicht bekannt, liefert die Frist bewusst kein Datum.
   *
   * @param year - Abrechnungsjahr (Monat, für den die Frist berechnet wird)
   * @param month - Abrechnungsmonat 1-12
   * @param deadline - die individuelle Deadline
   * @param customer - Kunde (nur für relative Fristen nötig, sonst optional)
   * @param bezugStatus - Monatsstatus desselben Monats für die Anlieferungskopplung
   */
  calculateIndividualDeadlineDate(
    year: number,
    month: number,
    deadline: CustomerIndividualDeadline,
    customer?: Customer | null,
    bezugStatus: CustomerMonthlyStatus | null = null
  ): Date | undefined {
    // Für einmalige Deadlines: IMMER anzeigen wenn nicht erledigt
    // Unabhängig vom aktuellen Arbeitsmonat
    if (!deadline.is_recurring && deadline.specific_date) {
      // Zeige die Deadline immer an, wenn sie nicht erledigt ist
      if (!deadline.completed) {
        return new Date(deadline.specific_date);
      }
      // Erledigte einmalige Deadlines werden nie angezeigt
      return undefined;
    }

    // Relative wiederkehrende Deadline: Bezug auf den effektiven Lohnlauf
    if (deadline.is_recurring && deadline.bezug_lohnlauf && customer) {
      const anker = calculateEffectiveLohnlaufDate(
        customer,
        deadline.bezug_lohnlauf as 1 | 2,
        year,
        month,
        bezugStatus
      );
      if (!anker) return undefined;
      return addWorkdays(anker, deadline.bezug_werktage_offset ?? 0);
    }
    
    // Für wiederkehrende Deadlines mit festem Tag: Bestehende Logik (monatsabhängig)
    if (deadline.is_recurring && deadline.day_of_month) {
      return calculateLohnlaufDate(
        year,
        month,
        deadline.day_of_month,
        deadline.verschiebung || null,
        deadline.monat_offset || 0
      );
    }

    return undefined;
  }

  /**
   * Backward compatibility: Erstellt eine neue individuelle Deadline (wiederkehrend)
   */
  async createIndividualDeadline(
    customerId: string,
    title: string,
    description: string | null,
    dayOfMonth: number,
    monatOffset: number = 0,
    verschiebung: 'frueher' | 'spaeter' | null = null
  ): Promise<CustomerIndividualDeadline | null> {
    // Delegate to new method for recurring deadlines
    return this.createRecurringDeadline(
      customerId,
      title,
      description,
      dayOfMonth,
      monatOffset,
      verschiebung
    );
  }
}

export const customerIndividualDeadlineService = new CustomerIndividualDeadlineService();
